namespace OSF {
  export let AddinIdInfo = function (addinIdString: string) {
    // Here is the format of the addinId and an example of it (ignore the whitespaces):
    //
    //      formatVersion | solutionId                           | solutionVersion | storeTypeId | storeLocation |||
    //                  2 | c5daabb3-e185-4e8c-adfc-1a7b7283fb6f | 1.0.0.0         | 5           | developer     |||
    //
    var addinIdItemSeparator = "|";
    var tokens = addinIdString.split(addinIdItemSeparator);
    var addinIdFormatVersion = "2";
    var formatVersionIndex = 0;
    var solutionIdIndex = 1;
    var solutionVersionIndex = 2;
    var storeTypeIdIndex = 3;
    var storeLocationIndex = 4;
    if (tokens.length != 8) {
      Telemetry.RuntimeTelemetryHelper.LogCommonMessageTag(
        "OSF.AddinIdInfo.constructor: Unexpected format of the addinId",
        null /*appCorrelationId*/,
        0x1f31c5c8 /* tag_5m2xi */,
      );
    }
    var _formatVersion = tokens[formatVersionIndex];
    var _solutionId = tokens[solutionIdIndex];
    var _solutionVersion = tokens[solutionVersionIndex];
    var _storeTypeId = tokens[storeTypeIdIndex];
    var _storeLocation = tokens[storeLocationIndex];
    var _compliantSolutionId = null;
    if (_formatVersion != addinIdFormatVersion) {
      Telemetry.RuntimeTelemetryHelper.LogCommonMessageTag(
        `OSF.AddinIdInfo.constructor: Unexpected format version: ${_formatVersion}.`,
        null /*appCorrelationId*/,
        0x1f31c5c9 /* tag_5m2xj */,
      );
    }
    return {
      getSolutionId: function OSF_AddinIdInfo$getSolutionId() {
        return _solutionId;
      },
      getSolutionVersion: function OSF_AddinIdInfo$getSolutionVersion() {
        return _solutionVersion;
      },
      getFormatVersion: function OSF_AddinIdInfo$getFormatVersion() {
        return _formatVersion;
      },
      getStoreTypeId: function OSF_AddinIdInfo$getStoreTypeId() {
        return _storeTypeId;
      },
      getStoreLocation: function OSF_AddinIdInfo$getStoreLocation() {
        return _storeLocation;
      },
      getPrivacyCompliantSolutionId: function OSF_AddinIdInfo$getPrivacyCompliantSolutionId() {
        // Bug#6244148 - AddinInfo always returning "Private" for SolutionIds from Centralized deployed addins
        //  This class doesn't support passing in the AssetId which is required to check if an Add in is public for the PrivateCatalog scenario.
        if (_compliantSolutionId == null) {
          _compliantSolutionId = Telemetry.PrivacyRules.GetPrivacyCompliantId(
            _solutionId,
            OfficeExt.ManifestUtil.GetStoreTypeName(_storeTypeId),
            null /* assetId */,
            null,
            null,
            _storeLocation,
          );
        }
        return _compliantSolutionId == null ? _solutionId : _compliantSolutionId;
      },

      needToRestoreManifest: function OSF_AddinIdInfo$needToRestoreManifest() {
        return _storeTypeId == OSF.StoreTypeEnum.Registry.toString();
      },
    };
  };

  /// <summary>
  /// manifest manager
  /// in charge of managing manifest retrieval and caching.
  /// </summary>
  /// <accessibility>public</accessibility>
  export let OsfManifestManager = (function () {
    var _cachedManifests = {};
    var _UILocale = "en-us";
    var _pendingRequests = {};
    /// <summary>
    /// help method to generate cache key.
    /// </summary>
    /// <accessibility>private</accessibility>
    /// <param name="marketplaceID">marketplaceID (type: string)</param>
    /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
    /// <returns>a string representing the generated cache key.</returns>
    function _generateKey(marketplaceID, marketplaceVersion) {
      return marketplaceID + "_" + marketplaceVersion;
    }
    return {
      /// <summary>
      /// get manifest, if it exists in the cache, just reuse it, otherwise retrieve it from corporate gallery.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="osfControl">osfControl (type: OsfControl)</param>
      /// <param name="onCompleted">callback for get manifest (type: Function)</param>
      getManifestAsync: function OSF_OsfManifestManager$getManifestAsync(context, onCompleted) {
        OSF.OUtil.validateParamObject(
          context,
          {
            osfControl: { type: Object, mayBeNull: false },
            referenceInUse: { type: Object, mayBeNull: false },
          },
          onCompleted,
        );

        var me = this;
        var reference = context.referenceInUse;
        var cacheManifestKey = reference.solutionId;
        if (
          context.osfControl != null &&
          context.osfControl._sharedRuntimeId != null &&
          context.osfControl._sharedRuntimeId.length > 0
        ) {
          cacheManifestKey = OSF.OsfManifestManager.getSharedRuntimeManifestCacheKey(
            reference.solutionId,
            context.osfControl._sharedRuntimeId,
          );
        }
        var cacheKey = _generateKey(cacheManifestKey, reference.version);
        var manifest = _cachedManifests[cacheKey];
        context.manifestCached = false;
        if (manifest) {
          context.manifestCached = true;
          if (me.manifestContainsDownloadResources(manifest)) {
            me.downloadResourcesAsync(context, onCompleted, manifest, cacheKey);
          } else {
            onCompleted({ statusCode: OSF.ProxyCallStatusCode.Succeeded, value: manifest, context: context });
          }
        } else if (context.clientEndPoint && context.manifestUrl) {
          Telemetry.AppLoadTimeHelper.ManifestRequestStart(context.osfControl._telemetryContext);
          var onRetrieveManifestCompleted = function (asyncResult) {
            if (asyncResult.statusCode === OSF.ProxyCallStatusCode.Succeeded && asyncResult.value) {
              var osfControl;
              try {
                osfControl = asyncResult.context.osfControl;
                var manifestString;
                if (typeof asyncResult.value === "string") {
                  //we have this check here to avoid breaking with old CC server that returns string instead of object
                  //TODO remove it later.
                  asyncResult.context.manifestCached = true; //for now, set cached flag to avoid showing warning for old CC server.
                  manifestString = asyncResult.value;
                } else {
                  asyncResult.context.manifestCached = asyncResult.value.cached;
                  manifestString = asyncResult.value.manifest;
                }
                Telemetry.AppLoadTimeHelper.SetManifestDataCachedFlag(
                  osfControl._telemetryContext,
                  asyncResult.value.cached,
                );
                asyncResult.value = new OSF.Manifest.Manifest(
                  manifestString,
                  osfControl._contextActivationMgr.getAppUILocale(),
                );
                OSF.OsfManifestManager.cacheManifest(reference.solutionId, reference.version, asyncResult.value);
              } catch (ex) {
                asyncResult.value = null;
                var appCorrelationId;
                if (osfControl) {
                  appCorrelationId = osfControl._appCorrelationId;
                }
                var message = "Invalid manifest in getManifestAsync.";
                OfficeExt.WACUtils.logExceptionToBrowserConsole(message, ex);
                Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
                  message,
                  ex,
                  appCorrelationId,
                  0x1f31c5ca /* tag_5m2xk */,
                );
              }
            } else {
              Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
                "getManifestAsync failed:" + OfficeExt.WACUtils.serializeObjectToString(asyncResult),
                null,
                null,
                0x1f31c5cb /* tag_5m2xl */,
              );
            }
            if (me.manifestContainsDownloadResources(manifest)) {
              me.downloadResourcesAsync(context, onCompleted, asyncResult.value, cacheKey);
            } else {
              onCompleted(asyncResult);
            }
          };
          var params = {
            manifestUrl: context.manifestUrl,
            id: reference.solutionId,
            version: reference.version,
            clearCache: context.clearCache || false,
          };
          this._invokeProxyMethodAsync(context, "OEM_getManifestAsync", onRetrieveManifestCompleted, params);
        } else {
          onCompleted({ statusCode: OSF.ProxyCallStatusCode.Failed, value: null, context: context });
        }
      },
      /// <summary>
      /// Safely checks if the given object is a manifest that contains download resources.
      /// This functions acts as a gate check to preserve old behavior for manifests that don't contain download resources.
      /// For now, we only handle ExtendedOverrides. CustomFunctions follow.
      /// </summary>
      manifestContainsDownloadResources: function OSF_OsfManifestManager$manifestContainsDownloadResources(manifest) {
        return this.manifestContainsExtendedOverrides(manifest);
      },
      /// <summary>
      /// Safely checks if the given object is a manifest that contains ExtendedOverrides.
      /// This functions acts as a gate check to preserve old behavior for manifests that don't contain ExtendedOverrides.
      /// </summary>
      manifestContainsExtendedOverrides: function OSF_OsfManifestManager$manifestContainsExtendedOverrides(manifest) {
        return (
          typeof manifest === "object" &&
          typeof manifest.getExtendedOverrides === "function" &&
          typeof manifest.getExtendedOverrides() === "object"
        );
      },
      /// <summary>
      /// Downloads and parses satellite resources if exist in the manifest.
      /// </summary>
      downloadResourcesAsync: function OSF_OsfManifestManager$downloadResourcesAsync(
        context,
        onCompleted,
        manifest,
        cacheKey,
      ) {
        if (this.manifestContainsExtendedOverrides(manifest)) {
          this.downloadExtendedOverridesAsync(context, onCompleted, manifest, cacheKey);
        }
      },
      /// <summary>
      /// Downloads and parses ExtendedOverrides if exists in the manifest.
      /// </summary>
      downloadExtendedOverridesAsync: function OSF_OsfManifestManager$downloadExtendedOverridesAsync(
        context,
        onCompleted,
        manifest,
        cacheKey,
      ) {
        var me = this;
        const completeWithSuccess = (context, onCompleted, manifest) => {
          onCompleted({ statusCode: OSF.ProxyCallStatusCode.Succeeded, value: manifest, context: context });
        };
        const completeWithError = (context, onCompleted, ex, message) => {
          const appCorrelationId = context.osfControl._appCorrelationId;
          OfficeExt.WACUtils.logExceptionToBrowserConsole(message, ex);
          Telemetry.RuntimeTelemetryHelper.LogExceptionTag(message, ex, appCorrelationId, 0x1f31c5cc /* tag_5m2xm */);
          // Remove from the manifest from cache if it's there.
          if (me._cachedManifests && me._cachedManifests[cacheKey]) {
            me._cachedManifests[cacheKey] = undefined;
          }
          onCompleted({ statusCode: OSF.ProxyCallStatusCode.Failed, value: null, context: context });
        };
        // Double-check the gating condition.
        if (!this.manifestContainsExtendedOverrides(manifest)) {
          completeWithError(context, onCompleted, "Unreachable", "Unreachable code path executed!");
        }
        try {
          let isInserted =
            context.osfControl.getInitializationReason() == Microsoft.Office.WebExtension.InitializationReason.Inserted;
          context.osfControl
            .processManifestForExtendedOverrides(manifest, isInserted)
            .then(() => completeWithSuccess(context, onCompleted, manifest))
            .catch((ex) => completeWithError(context, onCompleted, ex, "Invalid ExtendedOverrides content."));
        } catch (ex) {
          completeWithError(
            context,
            onCompleted,
            ex,
            "Unexpected exception from OsfControlV2.processManifestForExtendedOverrides()",
          );
        }
      },
      /// <summary>
      /// get app instance info from app instance id.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="context">context for this call</param>
      /// <param name="onCompleted">callback for get app instance info (type: Function)</param>
      getAppInstanceInfoByIdAsync: function OSF_OsfManifestManager$getAppInstanceInfoByIdAsync(context, onCompleted) {
        OSF.OUtil.validateParamObject(
          context,
          {
            webUrl: { type: String, mayBeNull: false },
            appInstanceId: { type: String, mayBeNull: false },
            clientEndPoint: { type: Object, mayBeNull: false },
          },
          onCompleted,
        );
        var params = {
          webUrl: context.webUrl,
          appInstanceId: context.appInstanceId,
          clearCache: context.clearCache || false,
        };
        this._invokeProxyMethodAsync(context, "OEM_getSPAppInstanceInfoByIdAsync", onCompleted, params);
      },
      /// <summary>
      /// get SP token from product id.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="context">context for this call</param>
      /// <param name="onCompleted">callback for get SP token (type: Function)</param>
      getSPTokenByProductIdAsync: function OSF_OsfManifestManager$getSPTokenByProductIdAsync(context, onCompleted) {
        OSF.OUtil.validateParamObject(
          context,
          {
            appWebUrl: { type: String, mayBeNull: false },
            productId: { type: String, mayBeNull: false },
          },
          onCompleted,
        );
        var me = this;
        var createSharePointProxyCompleted = function (clientEndPoint) {
          if (clientEndPoint) {
            var params = {
              webUrl: context.appWebUrl,
              productId: context.productId,
              clearCache: context.clearCache || false,
              clientEndPoint: clientEndPoint,
            };
            me._invokeProxyMethodAsync(context, "OEM_getSPTokenByProductIdAsync", onCompleted, params);
          } else {
            onCompleted({ statusCode: OSF.ProxyCallStatusCode.ProxyNotReady, value: null, context: context });
          }
        };
        context.osfControl._contextActivationMgr._createSharePointIFrameProxy(
          context.appWebUrl,
          createSharePointProxyCompleted,
        );
      },
      getSPAppEntitlementsAsync: function OSF_OsfManifestManager$getSPAppEntitlementsAsync(context, onCompleted) {
        OSF.OUtil.validateParamObject(
          context,
          {
            osfControl: { type: Object, mayBeNull: false },
            referenceInUse: { type: Object, mayBeNull: false },
            baseUrl: { type: String, mayBeNull: false },
            pageUrl: { type: String, mayBeNull: false },
            webUrl: { type: String, mayBeNull: true },
          },
          onCompleted,
        );
        if (!context.webUrl) {
          //webUrl hasn't been resolved from pageUrl yet
          var aElement = document.createElement("a");
          aElement.href = context.pageUrl;
          var pathName = aElement.pathname;
          var subPaths = pathName.split("/");
          var subPathCount = subPaths.length - 1;
          var path = aElement.href.substring(0, aElement.href.length - pathName.length);
          if (path && path.charAt(path.length - 1) !== "/") {
            path += "/";
          }
          var paths = [path];
          for (var i = 0; i < subPathCount; i++) {
            if (subPaths[i]) {
              path = path + subPaths[i] + "/";
              paths.push(path);
            }
          }
          aElement = null;
          var me = this;
          var contextActivationMgr = context.osfControl._contextActivationMgr;
          var baseUrl = paths.pop();
          var onResolvePageUrlCompleted = function (asyncResult) {
            if (asyncResult.statusCode === OSF.ProxyCallStatusCode.Succeeded) {
              var resolvedUrl = asyncResult.value;
              if (resolvedUrl && resolvedUrl.charAt(resolvedUrl.length - 1) !== "/") {
                resolvedUrl += "/";
              }
              contextActivationMgr._webUrl = resolvedUrl;
              asyncResult.context.webUrl = resolvedUrl;
              asyncResult.context.appWebUrl = resolvedUrl;
              me.getCorporateCatalogEntitlementsAsync(asyncResult.context, onCompleted);
            } else {
              //resolve web url from page url failed
              onCompleted(asyncResult);
            }
          };
          var createSPAppProxyCompleted = function (clientEndPoint) {
            if (clientEndPoint) {
              context.clientEndPoint = clientEndPoint;
              var params = { pageUrl: context.pageUrl, baseUrl: baseUrl, clearCache: context.clearCache || false };
              me._invokeProxyMethodAsync(
                context,
                "OEM_getSPAppWebUrlFromPageUrlAsync",
                onResolvePageUrlCompleted,
                params,
              );
            } else if (paths.length > 0) {
              baseUrl = paths.pop();
              contextActivationMgr._createSharePointIFrameProxy(baseUrl, createSPAppProxyCompleted);
            } else {
              onCompleted({ statusCode: OSF.ProxyCallStatusCode.Failed, value: null, context: context });
            }
          };
          contextActivationMgr._createSharePointIFrameProxy(baseUrl, createSPAppProxyCompleted);
        } else {
          this.getCorporateCatalogEntitlementsAsync(context, onCompleted);
        }
      },
      /// <summary>
      /// get corporate catalog entitlements
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="context">context for get entitlement call</param>
      /// <param name="onCompleted">callback (type: Function)</param>
      getCorporateCatalogEntitlementsAsync: function OSF_OsfManifestManager$getCorporateCatalogEntitlementsAsync(
        context,
        onCompleted,
      ) {
        OSF.OUtil.validateParamObject(
          context,
          {
            osfControl: { type: Object, mayBeNull: false },
            referenceInUse: { type: Object, mayBeNull: false },
            webUrl: { type: String, mayBeNull: false },
          },
          onCompleted,
        );
        // Corporate Authentication starts
        Telemetry.AppLoadTimeHelper.AuthenticationStart(context.osfControl._telemetryContext);
        var me = this;
        var retries = 0;
        var createSharePointProxyCompleted = function (clientEndPoint) {
          if (clientEndPoint) {
            // Corporate Authentication is done.
            Telemetry.AppLoadTimeHelper.AuthenticationEnd(context.osfControl._telemetryContext);
            context.clientEndPoint = clientEndPoint;
            var params = {
              webUrl: context.webUrl,
              applicationName: OSF.HostCapability[context.hostType],
              officeExtentionTarget:
                context.noTargetType ||
                context.osfControl.getOsfControlType() === OSF.OsfControlTarget.TaskPane /*TODO */
                  ? null
                  : context.osfControl.getOsfControlType(),
              // There are other type of Apps such as DictionaryApp we don't handle in the code right now. So we will not filter them out by putting TargetType as null for Taskpane only.
              clearCache: context.clearCache || false,
              supportedManifestVersions: {
                "1.0": true,
                "1.1": true,
              },
            };
            me._invokeProxyMethodAsync(context, "OEM_getEntitlementSummaryAsync", onCompleted, params);
          } else {
            if (retries < OSF.Constants.AuthenticatedConnectMaxTries) {
              retries++;
              setTimeout(function () {
                context.osfControl._contextActivationMgr._createSharePointIFrameProxy(
                  context.webUrl,
                  createSharePointProxyCompleted,
                );
              }, 500);
            } else {
              onCompleted({
                statusCode: OSF.ProxyCallStatusCode.ProxyNotReady,
                value: null,
                context: context,
              });
            }
          }
        };
        context.osfControl._contextActivationMgr._createSharePointIFrameProxy(
          context.webUrl,
          createSharePointProxyCompleted,
        );
      },
      _invokeProxyMethodAsync: function OSF_OsfManifestManager$_invokeProxyMethodAsync(
        context,
        methodName,
        onCompleted,
        params,
      ) {
        var clientEndPointUrl = params.clientEndPoint
          ? params.clientEndPoint._targetUrl
          : context.clientEndPoint._targetUrl;
        var requestKeyParts = [clientEndPointUrl, methodName];
        var runtimeType;
        for (var p in params) {
          runtimeType = typeof params[p];
          if (runtimeType === "string" || runtimeType === "number" || runtimeType === "boolean") {
            requestKeyParts.push(params[p]);
          }
        }
        var requestKey = requestKeyParts.join(".");
        var myPendingRequests = _pendingRequests;
        var newRequestHandler = { onCompleted: onCompleted, context: context, methodName: methodName };
        var pendingRequestHandlers = myPendingRequests[requestKey];
        if (!pendingRequestHandlers) {
          myPendingRequests[requestKey] = [newRequestHandler];
          var onMethodCallCompleted = function (errorCode, response) {
            var value = null;
            var statusCode = OSF.ProxyCallStatusCode.Failed;
            if (errorCode === 0 && response.status) {
              value = response.result;
              statusCode = OSF.ProxyCallStatusCode.Succeeded;
            }
            var currentPendingRequests = myPendingRequests[requestKey];
            delete myPendingRequests[requestKey];
            var pendingRequestHandlerCount = currentPendingRequests.length;
            for (var i = 0; i < pendingRequestHandlerCount; i++) {
              var currentRequestHandler = currentPendingRequests.shift();
              // Control id is optional, if this is a call associated to an app use it for logging information.  Some calls may have no control.
              var appCorrelationId;
              try {
                if (currentRequestHandler.context && currentRequestHandler.context.osfControl) {
                  appCorrelationId = currentRequestHandler.context.osfControl._appCorrelationId;
                }
                if (response && response.failureInfo) {
                  Telemetry.RuntimeTelemetryHelper.LogProxyFailure(
                    appCorrelationId,
                    currentRequestHandler.methodName,
                    response.failureInfo,
                  );
                }
                currentRequestHandler.onCompleted({
                  statusCode: statusCode,
                  value: value,
                  context: currentRequestHandler.context,
                });
              } catch (ex) {
                var message = "_invokeProxyMethodAsync failed.";
                OfficeExt.WACUtils.logExceptionToBrowserConsole(message, ex);
                Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
                  message,
                  ex,
                  appCorrelationId,
                  0x1f31c5cd /* tag_5m2xn */,
                );
              }
            }
          };
          //allow clientEndPoint override
          var clientEndPoint = context.clientEndPoint;
          if (params.clientEndPoint) {
            clientEndPoint = params.clientEndPoint;
            delete params.clientEndPoint;
          }
          // for all OMEX requests, pass in major.minor.revision as office version
          if (context.referenceInUse && context.referenceInUse.storeType === OSF.StoreType.OMEX) {
            params.officeVersion = OSF.Constants.ThreePartsFileVersion;
          }
          clientEndPoint.invoke(methodName, onMethodCallCompleted, params);
        } else {
          pendingRequestHandlers.push(newRequestHandler);
        }
      },
      removeOmexCacheAsync: function OSF_OsfManifestManager$removeOmexCacheAsync(context, onCompleted) {
        OSF.OUtil.validateParamObject(
          context,
          {
            osfControl: { type: Object, mayBeNull: false },
            referenceInUse: { type: Object, mayBeNull: false },
            clientEndPoint: { type: Object, mayBeNull: false },
          },
          onCompleted,
        );

        var reference = context.referenceInUse;
        var params: any = {
          applicationName: context.hostType,
          assetID: reference.solutionId,
          officeExtentionTarget: context.osfControl.getOsfControlType(),
          clearEntitlement: context.clearEntitlement || false,
          clearToken: context.clearToken || false,
          clearAppState: context.clearAppState || false,
          clearManifest: context.clearManifest || false,
          appVersion: context.appVersion,
        };
        if (context.anonymous) {
          params.contentMarket = reference.storeId;
        } else {
          params.assetContentMarket = context.osfControl._omexEntitlement.contentMarket;
          params.userContentMarket = reference.storeId;
        }
        this._invokeProxyMethodAsync(context, "OMEX_removeCacheAsync", onCompleted, params);
      },
      /// <summary>
      /// purge manifest from cache
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
      purgeManifest: function OSF_OsfManifestManager$purgeManifest(marketplaceID, marketplaceVersion) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "marketplaceVersion", type: String, mayBeNull: false },
        ]);
        if (e) throw e;
        var cacheKey = _generateKey(marketplaceID, marketplaceVersion);
        if (typeof _cachedManifests[cacheKey] != "undefined") {
          delete _cachedManifests[cacheKey];
        }
      },
      /// <summary>
      /// cache manifest from XML string (Pass in marketplaceID and marketplaceVersion explictly to work around issues
      /// where version known to host apps are different from version in the manfiest file. )
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
      /// <param name="manifestXml">The XML string of the manifest</param>
      /// <param name="uiLocale">The UI locale used to process the manifest</param>
      cacheManifestFromXml: function OSF_OsfManifestManager$cacheManifestFromXml(
        marketplaceID,
        marketplaceVersion,
        manifestXml,
        uiLocale,
      ) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "marketplaceVersion", type: String, mayBeNull: false },
          { name: "manifestXml", type: String, mayBeNull: false },
          { name: "uiLocale", type: String, mayBeNull: false },
        ]);
        if (e) throw e;
        this.cacheManifest(marketplaceID, marketplaceVersion, new OSF.Manifest.Manifest(manifestXml, uiLocale));
      },
      /// <summary>
      /// cache manifest (Pass in marketplaceID and marketplaceVersion explictly to work around issues
      /// where version known to host apps are different from version in the manfiest file. )
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
      /// <param name="manifest">manifest object</param>
      cacheManifest: function OSF_OsfManifestManager$cacheManifest(marketplaceID, marketplaceVersion, manifest) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "marketplaceVersion", type: String, mayBeNull: false },
          { name: "manifest", type: Object, mayBeNull: false },
        ]);
        if (e) throw e;
        var cacheKey = _generateKey(marketplaceID, marketplaceVersion);
        manifest._UILocale = manifest._UILocale || _UILocale;
        _cachedManifests[cacheKey] = manifest;
      },
      /// <summary>
      /// check to see if manifest is already cached.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
      hasManifest: function OSF_OsfManifestManager$hasManifest(marketplaceID, marketplaceVersion) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "marketplaceVersion", type: String, mayBeNull: false },
        ]);
        if (e) throw e;
        var cacheKey = _generateKey(marketplaceID, marketplaceVersion);
        if (typeof _cachedManifests[cacheKey] != "undefined") return true;
        return false;
      },
      /// <summary>
      /// check to see if manifest already exist for control
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="context">OSF control context</param>
      hasManifestForControl: function OSF_OsfManifestManager$hasManifestForControl(context) {
        OSF.OUtil.validateParamObject(
          context,
          {
            referenceInUse: { type: Object, mayBeNull: false },
          },
          null,
        );
        return this.hasManifest(context.referenceInUse.solutionId, context.referenceInUse.version);
      },
      /// <summary>
      /// get cached manifest.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="marketplaceVersion">marketplaceVersion (type: string)</param>
      getCachedManifest: function OSF_OsfManifestManager$getCachedManifest(marketplaceID, marketplaceVersion) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "marketplaceVersion", type: String, mayBeNull: false },
        ]);
        if (e) throw e;
        var cacheKey = _generateKey(marketplaceID, marketplaceVersion);
        return _cachedManifests[cacheKey];
      },
      /// <summary>
      /// get manifestCacheKey for sharedRuntime.
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="marketplaceID">marketplaceID (type: string)</param>
      /// <param name="sharedRuntimeId">sharedRuntimeId (type: string)</param>
      getSharedRuntimeManifestCacheKey: function OSF_OsfManifestManager$getSharedRuntimeManifestCacheKey(
        marketplaceID,
        sharedRuntimeId,
      ) {
        var e = Function._validateParams(arguments, [
          { name: "marketplaceID", type: String, mayBeNull: false },
          { name: "sharedRuntimeId", type: String, mayBeNull: false },
        ]);
        if (e) throw e;
        return marketplaceID + sharedRuntimeId;
      },
      /// <summary>
      /// is version1 less than version2
      /// </summary>
      /// <accessibility>public</accessibility>
      /// <param name="version1">version1 (type: string)</param>
      /// <param name="version2">version2 (type: string)</param>
      versionLessThan: function OSF_OsfManifestManager$versionLessThan(version1, version2) {
        var version1Parts = version1.split(".");
        var version2Parts = version2.split(".");
        var len = Math.min(version1Parts.length, version2Parts.length);
        var version1Part, version2Part, i;
        for (i = 0; i < len; i++) {
          try {
            version1Part = parseFloat(version1Parts[i]);
            version2Part = parseFloat(version2Parts[i]);
            if (version1Part != version2Part) {
              return version1Part < version2Part;
            }
          } catch (ex) {}
        }
        //all digits are the same except the remaining ones
        if (version1Parts.length >= version2Parts.length) {
          return false;
        } else {
          //version1Parts.length < version2Parts.length
          //handle the case like "12.0" vs. "12.0.0.0"
          len = version2Parts.length;
          var remainingSum = 0;
          for (i = version1Parts.length; i < len; i++) {
            try {
              version2Part = parseFloat(version2Parts[i]);
            } catch (ex) {
              version2Part = 0;
            }
            remainingSum += version2Part;
          }
          return remainingSum > 0;
        }
      },
      _setUILocale: function (UILocale) {
        _UILocale = UILocale;
      },
    };
  })();
}
